/**
 * 食事に食品を足す処理（食事シートの保存と、栄養ページのワンタップ記録で共通）と、その取り消し
 */

import { createMeal, deleteMeal, parseFoodText, regenerateMeal } from '@/api/services/nutrition'
import type { MealItemInput, MealLog, MealType } from '@/api/types/nutrition'

import { getNextMealType } from './utils'

/** 時刻から食事の種別を決める（〜10:30 朝食 / 〜15:00 昼食 / 〜17:00 間食 / 〜22:00 夕食 / それ以外 間食） */
export function mealTypeByTime(now: Date): MealType {
  const minutes = now.getHours() * 60 + now.getMinutes()
  if (minutes >= 4 * 60 && minutes < 10 * 60 + 30) return 'breakfast'
  if (minutes >= 10 * 60 + 30 && minutes < 15 * 60) return 'lunch'
  if (minutes >= 17 * 60 && minutes < 22 * 60) return 'dinner'
  return 'snack'
}

/** 今日なら時刻で、過去の日ならまだ記録していない最初の種別で決める */
export function guessQuickAddMealType(meals: MealLog[], isToday: boolean, now: Date = new Date()): MealType {
  return isToday ? mealTypeByTime(now) : getNextMealType(meals)
}

type QuickAddArgs = {
  date: string
  mealType: MealType
  /** "HH:mm:ss"。新しく食事を作るときだけ使う */
  eatenAt: string | null
  items: MealItemInput[]
  /** 画面に出ているその日の食事（新しく作ったかの判定に使う） */
  meals: MealLog[]
}

type QuickAddResult = {
  meal: MealLog
  undo: () => Promise<void>
}

/** 食事に食品を追記する（AI を通さない）。eatenAt を渡すと食事の時刻も更新する */
export async function addItemsToMeal(
  mealId: number,
  items: MealItemInput[],
  eatenAt?: string | null,
): Promise<MealLog | null> {
  if (items.length === 0) return null
  return regenerateMeal(mealId, eatenAt === undefined ? { add_items: items } : { add_items: items, eaten_at: eatenAt })
}

/** テキストか写真を AI で解析して、記録する食品の形にする（食事には保存しない） */
export async function parseAiItems(memo: string, image: File | undefined): Promise<MealItemInput[]> {
  const parsed = await parseFoodText(memo || undefined, image)
  return parsed.items.map(({ name, quantity, calories, protein_g, fat_g, carb_g }) => ({
    name,
    quantity,
    calories,
    protein_g,
    fat_g,
    carb_g,
  }))
}

/**
 * 食品をその日の食事に追加する。
 * 間食以外で同じ種別の食事があれば createMeal がそれを返すので、そこへ追記になる
 */
export async function quickAddItems({ date, mealType, eatenAt, items, meals }: QuickAddArgs): Promise<QuickAddResult> {
  const target = await createMeal({ date, meal_type: mealType, eaten_at: eatenAt })
  const createdNew = target.items.length === 0 && !meals.some((m) => m.id === target.id)
  const beforeIds = new Set(target.items.map((i) => i.id))

  const meal = (await addItemsToMeal(target.id, items)) ?? target
  const addedIds = meal.items.filter((i) => !beforeIds.has(i.id)).map((i) => i.id)

  const undo = async () => {
    if (createdNew) {
      await deleteMeal(meal.id)
      return
    }
    if (addedIds.length === 0) return
    await regenerateMeal(meal.id, { remove_item_ids: addedIds })
  }
  return { meal, undo }
}
